// src/pages/public/MyTickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Chip,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SortOutlinedIcon from "@mui/icons-material/SortOutlined";

import { ref, get, onValue } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";
import EmptyState from "../../components/ui/EmptyState";

// ─── design tokens ─────────────────────────────────────────────────────────────

const DIVIDER = "var(--linha)";
const GLASS_BG = "rgba(255,255,255,0.04)";
const WHITE_06 = "rgba(255,255,255,0.06)";
const WHITE_10 = "rgba(255,255,255,0.10)";
const WHITE_14 = "rgba(255,255,255,0.14)";
const WHITE_16 = "rgba(255,255,255,0.16)";
const WHITE_18 = "rgba(255,255,255,0.18)";
const WHITE_22 = "rgba(255,255,255,0.22)";
const BLACK_16 = "rgba(0,0,0,0.16)";
const BLACK_18 = "rgba(0,0,0,0.18)";
const GREEN_08 = "rgba(26,255,122,0.08)";
const GREEN_10 = "rgba(26,255,122,0.10)";
const GREEN_14 = "rgba(26,255,122,0.14)";
const GREEN_18 = "rgba(26,255,122,0.18)";
const GREEN_28 = "rgba(26,255,122,0.28)";
const GREEN_34 = "rgba(26,255,122,0.34)";
const GREEN_60 = "rgba(26,255,122,0.60)";
const GREEN_70 = "rgba(26,255,122,0.70)";

const STATUS_OPTS = ["Todos", "Válido", "Usado", "Cancelado"];

function fmt(n) {
  return Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function safeLower(v) {
  return (v || "").toString().toLowerCase().trim();
}

function statusLabel(s) {
  if (s === "valid") return "Válido";
  if (s === "used") return "Usado";
  if (s === "cancelled") return "Cancelado";
  return "Pendente";
}

function badgeSx(s) {
  const label = typeof s === "string" && s.length > 6 ? statusLabel(s) : s;
  if (label === "Válido")
    return { border: `1px solid ${GREEN_34}`, background: GREEN_14 };
  if (label === "Usado")
    return { border: `1px solid ${WHITE_18}`, background: WHITE_06 };
  return {
    border: "1px solid rgba(255,77,77,0.28)",
    background: "rgba(255,77,77,0.10)",
  };
}

function qrUrl(value) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(value)}`;
}

// ─── Componente principal ───────────────────────────────────────────────────────

export default function MyTickets() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [sort, setSort] = useState("purchased_desc");
  const [expanded, setExpanded] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // ─── carrega ingressos do RTDB ────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid) return;

    const indexRef = ref(rtdb, `ticketsByUser/${user.uid}`);

    const unsub = onValue(indexRef, async (snap) => {
      if (!snap.exists()) {
        setTickets([]);
        setLoading(false);
        return;
      }

      const ticketIds = Object.keys(snap.val());

      try {
        const snaps = await Promise.all(
          ticketIds.map((id) => get(ref(rtdb, `tickets/${id}`))),
        );

        const loaded = snaps
          .filter((s) => s.exists())
          .map((s) => s.val())
          .sort((a, b) => (b.purchasedAt || 0) - (a.purchasedAt || 0));

        setTickets(loaded);
      } catch (err) {
        console.error("Erro ao carregar ingressos:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [user?.uid]);

  // ─── filtros ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = safeLower(query);

    let list = tickets.filter((t) => {
      const label = statusLabel(t.status);
      const matchQuery =
        !q ||
        safeLower(t.eventTitle).includes(q) ||
        safeLower(t.ticketTypeName).includes(q) ||
        safeLower(t.eventCity).includes(q) ||
        safeLower(t.orderId).includes(q) ||
        safeLower(t.ticketId).includes(q);

      const matchStatus =
        statusFilter === "Todos" ? true : label === statusFilter;

      return matchQuery && matchStatus;
    });

    if (sort === "upcoming") {
      list = [...list].sort(
        (a, b) => (a.eventStartAtMs || 0) - (b.eventStartAtMs || 0),
      );
    }
    if (sort === "purchased_desc") {
      list = [...list].sort((a, b) => (b.purchasedAt || 0) - (a.purchasedAt || 0));
    }
    if (sort === "price_desc") {
      list = [...list].sort((a, b) => (b.totalPaid || 0) - (a.totalPaid || 0));
    }

    return list;
  }, [tickets, query, statusFilter, sort]);

  const metrics = useMemo(() => {
    const total = tickets.length;
    const valid = tickets.filter((t) => t.status === "valid").length;
    const used = tickets.filter((t) => t.status === "used").length;
    const spent = tickets.reduce((acc, t) => acc + (t.totalPaid || 0), 0);
    return { total, valid, used, spent };
  }, [tickets]);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {}
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1200);
  };

  // ─── estilos de input ─────────────────────────────────────────────────────
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.2,
      background: BLACK_18,
      "& fieldset": { borderColor: DIVIDER },
      "&:hover fieldset": { borderColor: WHITE_22 },
      "&.Mui-focused fieldset": { borderColor: GREEN_70 },
    },
    "& .MuiInputBase-input::placeholder": { opacity: 0.7 },
  };

  const selectSx = {
    borderRadius: 2.2,
    background: BLACK_18,
    "& .MuiSelect-select": { pl: 1.5 },
    "& fieldset": { borderColor: DIVIDER },
    "&:hover fieldset": { borderColor: WHITE_22 },
    "&.Mui-focused fieldset": { borderColor: GREEN_70 },
  };

  const heroBg = `
    radial-gradient(980px 420px at 12% 22%, ${GREEN_18}, transparent 60%),
    radial-gradient(820px 420px at 92% 8%, ${GREEN_10}, transparent 55%),
    linear-gradient(180deg, ${GLASS_BG}, transparent)
  `;

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: { xs: 6, md: 8 } }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 5 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: heroBg,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth={false} sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}>
          <Slide direction="up" in timeout={520}>
            <Box>
              <Typography
                sx={{ fontWeight: 900, letterSpacing: "0.14em", fontSize: 12, opacity: 0.8, mb: 1 }}
              >
                MINHA CONTA
              </Typography>
              <Typography
                variant="h2"
                sx={{ fontWeight: 950, letterSpacing: "-0.03em", fontSize: { xs: 34, sm: 42, md: 50 }, lineHeight: 1.05, mb: 1.2 }}
              >
                Meus ingressos
              </Typography>
              <Typography sx={{ maxWidth: "70ch", opacity: 0.78, lineHeight: 1.7 }}>
                Aqui ficam seus ingressos comprados. Você pode ver detalhes, copiar
                o código e abrir o QR para check-in.
              </Typography>
            </Box>
          </Slide>

          <Fade in timeout={720}>
            <Box
              sx={{
                mt: 3,
                p: { xs: 1.3, sm: 1.6 },
                borderRadius: 1,
                border: `1px solid ${DIVIDER}`,
                background: GLASS_BG,
                boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
                backdropFilter: "blur(16px)",
              }}
            >
              {/* métricas */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<ConfirmationNumberOutlinedIcon />}
                    label={`Total: ${metrics.total}`}
                    sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06 }}
                  />
                  <Chip
                    label={`Válidos: ${metrics.valid}`}
                    sx={{ borderRadius: 0.5, fontWeight: 900, ...badgeSx("Válido") }}
                  />
                  <Chip
                    label={`Usados: ${metrics.used}`}
                    sx={{ borderRadius: 0.5, fontWeight: 900, ...badgeSx("Usado") }}
                  />
                  <Chip
                    label={`Gasto: ${fmt(metrics.spent)}`}
                    sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${GREEN_28}`, background: GREEN_10 }}
                  />
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 0.5, fontWeight: 900, borderColor: WHITE_22, "&:hover": { borderColor: GREEN_60, background: GREEN_08 } }}
                  onClick={() => { setQuery(""); setStatusFilter("Todos"); setSort("upcoming"); }}
                >
                  Limpar filtros
                </Button>
              </Stack>

              <Divider sx={{ my: 1.4, opacity: 0.25, borderColor: DIVIDER }} />

              {/* filtros */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ xs: "stretch", md: "center" }}>
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por evento, tipo de ingresso, pedido..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ opacity: 0.85 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <FormControl sx={{ minWidth: { xs: "100%", md: 200 } }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    startAdornment={<InputAdornment position="start" sx={{ ml: 1 }}><EventOutlinedIcon sx={{ opacity: 0.85 }} /></InputAdornment>}
                    sx={selectSx}
                  >
                    {STATUS_OPTS.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: { xs: "100%", md: 220 } }}>
                  <Select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    startAdornment={<InputAdornment position="start" sx={{ ml: 1 }}><SortOutlinedIcon sx={{ opacity: 0.85 }} /></InputAdornment>}
                    sx={selectSx}
                  >
                    <MenuItem value="upcoming">Mais próximos</MenuItem>
                    <MenuItem value="purchased_desc">Compra mais recente</MenuItem>
                    <MenuItem value="price_desc">Maior valor</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* LISTA */}
      <Container maxWidth={false} sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}>
        {loading ? (
          <Box sx={{ py: 10, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={tickets.length === 0 ? "Nenhum ingresso ainda" : "Nenhum resultado"}
            subtitle={
              tickets.length === 0
                ? "Compre ingressos para seus eventos favoritos e eles aparecerão aqui."
                : "Tente remover os filtros."
            }
            actionLabel="Ver eventos"
            actionTo="/eventos"
          />
        ) : (
          <Stack spacing={2}>
            {filtered.map((t, idx) => {
              const isOpen = expanded === t.ticketId;
              const label = statusLabel(t.status);
              const dateLabel = t.eventStartAtMs
                ? new Date(t.eventStartAtMs).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "Data a definir";
              const locationLabel =
                t.eventCity && t.eventState
                  ? `${t.eventCity} · ${t.eventState}`
                  : t.eventCity || "";

              return (
                <Slide key={t.ticketId} direction="up" in timeout={380 + idx * 70}>
                  <Fade in timeout={520 + idx * 70}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
                        borderRadius: 1,
                        overflow: "hidden",
                        border: `1px solid ${DIVIDER}`,
                        background: GLASS_BG,
                        boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                        transition: "all .3s ease",
                        "&:hover": {
                          transform: { md: "translateY(-4px)" },
                          boxShadow: "0 16px 60px rgba(0,0,0,0.34)",
                          borderColor: WHITE_18,
                        },
                      }}
                    >
                      {/* POSTER */}
                      <Box
                        sx={{
                          minHeight: { xs: 150, md: "100%" },
                          backgroundImage: t.eventBannerUrl
                            ? `url(${t.eventBannerUrl})`
                            : "linear-gradient(135deg, #0d1f3c, #1a3a6e)",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(180deg, rgba(0,0,0,0.28), rgba(0,0,0,0.65))",
                          }}
                        />
                        <Box sx={{ position: "absolute", left: 14, top: 14, display: "flex", gap: 1 }}>
                          <Chip
                            label={label}
                            sx={{ borderRadius: 0.5, fontWeight: 950, ...badgeSx(label) }}
                          />
                          <Chip
                            label={t.ticketTypeName}
                            sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_16}`, background: WHITE_06 }}
                          />
                        </Box>
                        <Box sx={{ position: "absolute", left: 14, right: 14, bottom: 14 }}>
                          <Typography sx={{ fontWeight: 950, fontSize: 17, lineHeight: 1.2 }}>
                            {t.eventTitle}
                          </Typography>
                          <Typography sx={{ fontSize: 13, opacity: 0.82, mt: 0.4 }}>
                            {dateLabel}
                          </Typography>
                          {locationLabel && (
                            <Typography sx={{ fontSize: 13, opacity: 0.72, mt: 0.2 }}>
                              {locationLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* BODY */}
                      <Box sx={{ p: { xs: 1.6, md: 2 }, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Stack spacing={0.4}>
                            <Typography sx={{ opacity: 0.65, fontSize: 11, letterSpacing: "0.12em", fontWeight: 900 }}>
                              INGRESSO · {t.ticketId.slice(0, 16)}...
                            </Typography>
                            <Typography sx={{ fontWeight: 950, fontSize: 18 }}>
                              {fmt(t.totalPaid)}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {locationLabel && (
                                <Chip
                                  icon={<PlaceOutlinedIcon />}
                                  label={locationLabel}
                                  size="small"
                                  sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06 }}
                                />
                              )}
                            </Stack>
                          </Stack>

                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<OpenInNewRoundedIcon />}
                              onClick={() => navigate(`/eventos/${t.eventId}`)}
                              sx={{ borderRadius: 0.5, fontWeight: 900, borderColor: WHITE_22, "&:hover": { borderColor: GREEN_60, background: GREEN_08 } }}
                            >
                              Ver evento
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<QrCode2RoundedIcon />}
                              disabled={t.status !== "valid"}
                              onClick={() => setExpanded(isOpen ? null : t.ticketId)}
                              sx={{ borderRadius: 0.5, fontWeight: 950, boxShadow: "none" }}
                            >
                              QR / Detalhes
                            </Button>
                          </Stack>
                        </Stack>

                        <Divider sx={{ my: 1.4, opacity: 0.25, borderColor: DIVIDER }} />

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                          <Chip
                            label={`Pedido: ${t.orderId.slice(0, 16)}…`}
                            size="small"
                            sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06, maxWidth: "100%" }}
                          />
                          <Chip
                            label={t.lotName}
                            size="small"
                            sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06 }}
                          />
                          <Chip
                            label={`Unitário: ${fmt(t.unitPrice)}`}
                            size="small"
                            sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06 }}
                          />
                          <Chip
                            label={`Taxa ONURB: ${fmt(t.feeAmount)}`}
                            size="small"
                            sx={{ borderRadius: 0.5, fontWeight: 900, border: `1px solid ${WHITE_14}`, background: WHITE_06 }}
                          />
                        </Stack>

                        {/* expandido: QR + código */}
                        <Collapse in={isOpen} timeout={280} unmountOnExit>
                          {/* picote */}
                          <Box
                            sx={{
                              mt: 2,
                              mx: -2,
                              height: 0,
                              borderTop: `2px dashed rgba(255,255,255,0.12)`,
                              position: "relative",
                              "&::before, &::after": {
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                background: "var(--bg, #141414)",
                              },
                              "&::before": { left: -9 },
                              "&::after": { right: -9 },
                            }}
                          />

                          <Box
                            sx={{
                              pt: 2.5,
                              display: "flex",
                              gap: { xs: 2, sm: 3 },
                              flexDirection: { xs: "column", sm: "row" },
                              alignItems: { xs: "stretch", sm: "flex-start" },
                            }}
                          >
                            {/* QR image */}
                            <Box sx={{ flexShrink: 0, textAlign: "center" }}>
                              <Box
                                sx={{
                                  p: 1.2,
                                  background: "#fff",
                                  borderRadius: 2,
                                  width: { xs: 200, sm: 152 },
                                  height: { xs: 200, sm: 152 },
                                  mx: "auto",
                                  boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={qrUrl(t.qrValue)}
                                  alt="QR Code"
                                  sx={{ width: "100%", height: "100%", display: "block", borderRadius: 1 }}
                                />
                              </Box>
                              <Typography sx={{ mt: 1, fontSize: 10, opacity: 0.45, letterSpacing: "0.1em", fontWeight: 700 }}>
                                CHECK-IN
                              </Typography>
                            </Box>

                            {/* info lateral */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 950, fontSize: 13, mb: 0.4, opacity: 0.9 }}>
                                Código de acesso
                              </Typography>
                              <Typography sx={{ opacity: 0.55, fontSize: 12, mb: 1.5, lineHeight: 1.5 }}>
                                Apresente este QR Code na entrada do evento para realizar o check-in.
                              </Typography>

                              {/* hash do QR */}
                              <Box
                                sx={{
                                  p: "10px 14px",
                                  borderRadius: 1.5,
                                  border: `1px solid ${WHITE_10}`,
                                  background: "rgba(0,0,0,0.28)",
                                  mb: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minWidth: 0,
                                  overflow: "hidden",
                                }}
                              >
                                <QrCode2RoundedIcon sx={{ fontSize: 16, opacity: 0.45, flexShrink: 0 }} />
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: 10.5,
                                    opacity: 0.65,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {t.qrValue}
                                </Typography>
                              </Box>

                              {/* botão copiar */}
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={
                                  copiedId === t.ticketId
                                    ? <DoneRoundedIcon sx={{ fontSize: 15 }} />
                                    : <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
                                }
                                onClick={() => copy(t.qrValue, t.ticketId)}
                                sx={{
                                  mb: 1.5,
                                  borderRadius: 1.5,
                                  fontWeight: 900,
                                  fontSize: 12,
                                  borderColor: copiedId === t.ticketId ? GREEN_60 : WHITE_22,
                                  color: copiedId === t.ticketId ? "var(--verdeClaro)" : "inherit",
                                  background: copiedId === t.ticketId ? GREEN_08 : "transparent",
                                  transition: "all .2s",
                                  "&:hover": { borderColor: GREEN_60, background: GREEN_08 },
                                }}
                              >
                                {copiedId === t.ticketId ? "Copiado!" : "Copiar código"}
                              </Button>

                              {/* detalhes adicionais */}
                              <Stack spacing={0.6}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: 12, opacity: 0.55, flexShrink: 0 }}>ID do ingresso</Typography>
                                  <Typography sx={{ fontSize: 12, fontFamily: "monospace", opacity: 0.8, fontWeight: 700, textAlign: "right" }}>
                                    {t.ticketId ? `${t.ticketId.slice(0, 18)}…` : "—"}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: 12, opacity: 0.55, flexShrink: 0 }}>Comprado em</Typography>
                                  <Typography sx={{ fontSize: 12, opacity: 0.8, fontWeight: 700, textAlign: "right" }}>
                                    {t.purchasedAt
                                      ? new Date(t.purchasedAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
                                      : "—"}
                                  </Typography>
                                </Box>
                                {t.checkinAt && (
                                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 12, opacity: 0.55, flexShrink: 0 }}>Check-in</Typography>
                                    <Typography sx={{ fontSize: 12, color: "var(--verdeClaro)", fontWeight: 900, textAlign: "right" }}>
                                      {new Date(t.checkinAt).toLocaleString("pt-BR")}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  </Fade>
                </Slide>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
